import type * as CSS from 'csstype';
import {
  DashboardCumulativeProfitability,
  DashboardProfitability,
  ProfitabilityChangeGroup,
  ProfitabilityCumulativeChangeGroup,
} from 'enums';

interface LegendTheme {
  background: CSS.Property.Color;
  width: CSS.Property.Width;
  $iconColor: CSS.Property.Color;
  $iconHoverColor: CSS.Property.Color;
  $iconHoverOpacity: CSS.Property.Opacity;
}

type DashboardTheme = Record<ProfitabilityChangeGroup, { legend: LegendTheme }>;

type DefaultOverrides = Partial<
  Record<ProfitabilityChangeGroup, Partial<LegendTheme>>
>;

const palette = {
  darkBlue: 'rgb(39, 97, 149)',
  grey: 'rgb(204, 204, 204)',
  lightBlue: '#5177A8',
  lightGreen: 'rgb(171, 227, 156)',
  lightPink: 'rgb(243, 184, 180)',
};

const legendDefaults: LegendTheme = {
  background: '#fff',
  width: '160px',
  $iconColor: '#fff',
  $iconHoverColor: '#fff',
  $iconHoverOpacity: 0.6,
};

const getCumulativeTheme = () => {
  return {
    [ProfitabilityCumulativeChangeGroup.CUMULATIVE_PROFIT]: {
      legend: {
        ...legendDefaults,
        background: palette.lightBlue,
        color: '#fff',
      },
    },
    [ProfitabilityCumulativeChangeGroup.POSITIVE_PROFIT]: {
      legend: {
        ...legendDefaults,
        background: palette.lightGreen,
      },
    },
    [ProfitabilityCumulativeChangeGroup.NEGATIVE_PROFIT]: {
      legend: {
        ...legendDefaults,
        background: palette.lightPink,
      },
    },
    [ProfitabilityCumulativeChangeGroup.RETAINED_PROFIT]: {
      legend: {
        ...legendDefaults,
        background: palette.grey,
      },
    },
  };
};

const getTheme = (overrides: DefaultOverrides, initTheme?: DashboardTheme) => {
  return {
    [ProfitabilityChangeGroup.PROFIT]: {
      legend: {
        ...legendDefaults,
        ...(initTheme || {})[ProfitabilityChangeGroup.PROFIT]?.legend,
        ...overrides[ProfitabilityChangeGroup.PROFIT],
      },
    },
    [ProfitabilityChangeGroup.LOSS]: {
      legend: {
        ...legendDefaults,
        ...(initTheme || {})[ProfitabilityChangeGroup.LOSS]?.legend,
        ...overrides[ProfitabilityChangeGroup.LOSS],
      },
    },
    [ProfitabilityChangeGroup.COST]: {
      legend: {
        ...legendDefaults,
        ...(initTheme || {})[ProfitabilityChangeGroup.COST]?.legend,
        ...overrides[ProfitabilityChangeGroup.COST],
      },
    },
    [ProfitabilityChangeGroup.TOTAL_REVENUE]: {
      legend: {
        ...legendDefaults,
        ...(initTheme || {})[ProfitabilityChangeGroup.TOTAL_REVENUE]?.legend,
        ...overrides[ProfitabilityChangeGroup.TOTAL_REVENUE],
      },
    },
  };
};

const getCustomerProfitabilityTheme = () => {
  const updates = {
    [ProfitabilityChangeGroup.PROFIT]: {
      background: palette.lightGreen,
    },
    [ProfitabilityChangeGroup.LOSS]: {
      background: palette.lightPink,
    },
    [ProfitabilityChangeGroup.COST]: {
      background: palette.lightPink,
    },
    [ProfitabilityChangeGroup.TOTAL_REVENUE]: {
      background: palette.grey,
    },
  };
  const legendTheme = getTheme(updates);
  return legendTheme;
};

const getCompanyDetailTheme = (initTheme?: ReturnType<typeof getTheme>) => {
  const updates = {
    [ProfitabilityChangeGroup.PROFIT]: {
      background: palette.lightBlue,
      color: '#fff',
    },
  };
  const legendTheme = getTheme(updates, initTheme);
  return legendTheme;
};

const makeProfitabilityTheme = () => {
  const customerProfitabilityTheme = getCustomerProfitabilityTheme();
  const companyDetailProfitabilityTheme = getCompanyDetailTheme(
    customerProfitabilityTheme,
  );
  const cumulativeTheme = getCumulativeTheme();
  const profitabilityTheme = {
    [DashboardProfitability.PROFITABILITY_ACCOUNT]:
      companyDetailProfitabilityTheme,
    [DashboardProfitability.PROFITABILITY_CUSTOMER]: customerProfitabilityTheme,
    [DashboardProfitability.PROFITABILITY_COMPANY_DETAIL]:
      companyDetailProfitabilityTheme,
    [DashboardCumulativeProfitability.PROFITABILITY_CUMULATIVE_COMPANY_DETAIL]:
      cumulativeTheme,
  };

  return profitabilityTheme;
};

export default makeProfitabilityTheme;
